import {
	UntypedFormArray,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { CustomReportTable } from './custom-report-table.model';
import { Validation } from './validation.model';

export interface CustomReportDisplayFieldJSON {
	id: number;
	customReportId: number;
	displayCategory: string;
	displayColumns: string;
	sequence: number;
}

export class CustomReportDisplayField {
	constructor(
		public id: number,
		public customReportId: number,
		public displayCategory: string,
		public displayColumns: string,
		public sequence: number
	) {}

	static fromJSON(
		json: CustomReportDisplayFieldJSON
	): CustomReportDisplayField {
		return Object.assign(
			Object.create(CustomReportDisplayField.prototype),
			json,
			{}
		);
	}

	static createFormArray(
		fields: CustomReportDisplayField[],
		tables: CustomReportTable[],
		validation: Validation
	): UntypedFormArray {
		return fields
			? new UntypedFormArray(
					fields.map((field) =>
						this.createFormGroup(field, tables, validation)
					)
				)
			: new UntypedFormArray([]);
	}

	static createFormGroup(
		field: CustomReportDisplayField,
		tables: CustomReportTable[],
		_validation: Validation
	): UntypedFormGroup {
		const fg: UntypedFormGroup = new UntypedFormGroup({
			id: new UntypedFormControl(field ? field.id : null),
			customReportId: new UntypedFormControl(
				field ? field.customReportId : null
			),
			displayCategory: new UntypedFormControl(
				field ? field.displayCategory : null,
				[Validators.required]
			),
			displayColumns: new UntypedFormControl(
				field ? field.displayColumns : null,
				[Validators.required]
			),
			selectedFields: new UntypedFormControl(null, [Validators.required]),
			sequence: new UntypedFormControl(field ? field.sequence : null),
		});
		fg.get('selectedFields').disable();
		fg.get('displayCategory')
			.valueChanges.pipe(
				startWith(fg.get('displayCategory').value),
				distinctUntilChanged()
			)
			.subscribe((value) => {
				fg.get('displayColumns').setValue(
					field && field.displayCategory === value
						? field.displayColumns
						: null
				);
			});
		fg.get('displayColumns')
			.valueChanges.pipe(startWith(fg.get('displayColumns').value))
			.subscribe((value) => {
				fg.get('selectedFields').setValue(
					CustomReportTable.convertFieldListToAliasString(
						value,
						fg.get('displayCategory').value,
						tables
					)
				);
			});
		return fg;
	}

	static convertFieldNamesToDisplayColumns(fieldNames: string[]): string {
		const joined: string = fieldNames.join(',');
		return joined.length > 0 ? joined : null;
	}

	static fromFormArray(fa: UntypedFormArray): CustomReportDisplayField[] {
		return fa.length > 0
			? fa.controls.map((fg: UntypedFormGroup) => this.fromFormGroup(fg))
			: [];
	}

	static fromFormGroup(fg: UntypedFormGroup): CustomReportDisplayField {
		return Object.assign(
			Object.create(CustomReportDisplayField.prototype),
			{
				id: fg.get('id').value,
				displayCategory: fg.get('displayCategory').value,
				displayColumns: fg.get('displayColumns').value,
			}
		);
	}
}
